import React from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  ButtonToolbar,
  Dropdown,
  Glyphicon,
  MenuItem,
} from 'react-bootstrap';
import { Field, reduxForm, change } from 'redux-form';
import { connect } from 'react-redux';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  intlShape,
} from 'react-intl';

import render from 'components/ReduxForm/renderField';
import hoursEntryTypes from 'core/hoursEntryTypes';
import { dateOnlyToString } from '../../core/dateonly';

const years = [2016, 2017, 2018, 2019, 2020];
const DropdownMenu = Dropdown.Menu;
const DropdownToggle = Dropdown.Toggle;

export const messages = defineMessages({
  year: {
    id: 'filterForm.year',
    defaultMessage: 'Year',
    description: 'Label for year field in filter form',
  },
  from: {
    id: 'controlling.from',
    defaultMessage: 'From',
    description: 'Label for From field in filter form',
  },
  to: {
    id: 'controlling.to',
    defaultMessage: 'To',
    description: 'Label for To field in filter form',
  },
  project: {
    id: 'filterForm.project',
    defaultMessage: 'Project',
    description: 'Label for project field in filter form',
  },
  category: {
    id: 'filterForm.category',
    defaultMessage: 'Category',
    description: 'Label for category in filter form',
  },
  type: {
    id: 'filterForm.type',
    defaultMessage: 'Type',
    description: 'Label for type field in filter form',
  },
  shortWork: {
    id: 'filterForm.shortWork',
    defaultMessage: 'Short Time Work',
    description: 'Label for shortWork field in filter form',
  },
  submit: {
    id: 'filterForm.submit',
    defaultMessage: 'Apply',
    description: 'Submit button text in filter form',
  },
  reset: {
    id: 'filterForm.reset',
    defaultMessage: 'Reset',
    description: 'Reset button text in filter form',
  },
  applyCompensation: {
    id: 'filterForm.applyCompensation',
    defaultMessage: 'Apply Compensation',
    description: 'Apply Compensation button text in filter form',
  },
  exportFormat: {
    id: 'filterForm.exportFormat',
    defaultMessage: 'Export',
    description: 'Export dropdown text in filter form',
  },
});

class FilterForm extends React.Component {
  static propTypes = {
    projects: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    ),
    shortWorkEntries: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        start: PropTypes.string.isRequired,
        end: PropTypes.string.isRequired,
        active: PropTypes.bool.isRequired,
      }),
    ),
    handleSubmit: PropTypes.func.isRequired,
    submitting: PropTypes.bool.isRequired,
    onReset: PropTypes.func.isRequired,
    onExport: PropTypes.func,
    intl: intlShape.isRequired,
    dispatch: PropTypes.func.isRequired,
  };

  static defaultProps = {
    projects: [],
    onExport: null,
    shortWorkEntries: [],
  };

  constructor(props) {
    super(props);

    this.handleDatepickerChange = this.handleDatepickerChange.bind(this);
    this.handleYearChange = this.handleYearChange.bind(this);
    this.handleShortWorkChange = this.handleShortWorkChange.bind(this);
    this.handleExportFormatChange = this.handleDatepickerChange.bind(this);

    this.yearSelectRef = React.createRef();
    this.typeSelectRef = React.createRef();
    this.exportSelectRef = React.createRef();
    this.shortWorkSelectRef = React.createRef();
  }

  handleYearChange(e, value) {
    if (value) {
      this.props.dispatch(change('filterForm', 'from', null));
      this.props.dispatch(change('filterForm', 'to', null));
      this.props.dispatch(change('filterForm', 'shortWork', null));
    }
  }

  handleDatepickerChange(e, value) {
    if (value) {
      this.props.dispatch(change('filterForm', 'year', null));
    }
  }

  handleShortWorkChange(e, value) {
    if (value) {
      const { shortWorkEntries } = this.props;
      const shortWorkEntry = shortWorkEntries.find(
        entry => entry.id === value.value,
      );
      if (shortWorkEntry) {
        const from = dateOnlyToString(shortWorkEntry.start);
        const to = dateOnlyToString(shortWorkEntry.end);
        this.props.dispatch(change('filterForm', 'from', from));
        this.props.dispatch(change('filterForm', 'to', to));
      } else {
        this.props.dispatch(change('filterForm', 'from', null));
        this.props.dispatch(change('filterForm', 'to', null));
      }

      this.props.dispatch(change('filterForm', 'year', null));
    }
  }

  handleExportFormatChange(e, value) {
    if (value) {
      this.props.onExport(e, value);
    }
  }

  render() {
    const { projects, shortWorkEntries } = this.props;
    const activeShortWorkEntries = shortWorkEntries.filter(e => e.active);
    const showShortWorkEntries =
      activeShortWorkEntries && activeShortWorkEntries.length > 0;
    const exportFormats = [
      {
        label: 'PDF',
        value: 'pdf',
      },
      {
        label: 'XLSX',
        value: 'xlsx',
      },
    ];
    const formats = exportFormats.map(fmt => (
      <MenuItem key={fmt.value} eventKey={fmt.value}>
        {fmt.label}
      </MenuItem>
    ));
    return (
      <form onSubmit={this.props.handleSubmit}>
        <fieldset>
          <Row>
            <Col xs={12} md={2}>
              <Field
                id="year"
                name="year"
                onChange={(e, value) => this.handleYearChange(e, value)}
                label={<FormattedMessage {...messages.year} />}
                component={render.renderSelectAlt}
                options={years.map(year => ({
                  label: year,
                  value: year,
                }))}
                selectRef={this.yearSelectRef}
              />
            </Col>
            <Col xs={12} md={2}>
              <Field
                id="from"
                name="from"
                onChange={(e, value) => this.handleDatepickerChange(e, value)}
                label={<FormattedMessage {...messages.from} />}
                component={render.renderDate}
              />
            </Col>
            <Col xs={12} md={2}>
              <Field
                id="to"
                name="to"
                label={<FormattedMessage {...messages.to} />}
                component={render.renderDate}
              />
            </Col>
            {projects &&
              projects.length > 0 && (
                <Col xs={12} md={showShortWorkEntries ? 2 : 3}>
                  <Field
                    id="project"
                    name="project"
                    label={<FormattedMessage {...messages.project} />}
                    component={render.renderProjectSelect}
                    projects={projects}
                  />
                </Col>
              )}
            <Col xs={12} md={showShortWorkEntries ? 2 : 3}>
              <Field
                id="type"
                name="type"
                label={<FormattedMessage {...messages.type} />}
                component={render.renderSelectAlt}
                options={Object.values(hoursEntryTypes).map(type => ({
                  value: type,
                  label: type,
                }))}
                selectRef={this.typeSelectRef}
              />
            </Col>
            {showShortWorkEntries && (
              <Col xs={12} md={2}>
                <Field
                  id="shortWork"
                  name="shortWork"
                  onChange={(e, value) => this.handleShortWorkChange(e, value)}
                  label={<FormattedMessage {...messages.shortWork} />}
                  component={render.renderSelectAlt}
                  options={activeShortWorkEntries.map(entry => ({
                    value: entry.id,
                    label: `${entry.name}: ${dateOnlyToString(
                      entry.start,
                    )} - ${dateOnlyToString(entry.end)}`,
                  }))}
                  selectRef={this.shortWorkSelectRef}
                />
              </Col>
            )}
          </Row>
          <ButtonToolbar>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={this.props.submitting}
            >
              <FormattedMessage {...messages.submit} />
            </button>
            <button
              type="button"
              className="btn"
              disabled={this.props.submitting}
              onClick={this.props.onReset}
            >
              <FormattedMessage {...messages.reset} />
            </button>
            {this.props.onExport && (
              <Dropdown
                id="exportDropdown"
                onSelect={(e, value) => this.props.onExport(e, value)}
              >
                <DropdownToggle className="btn dropdown-toggle">
                  <Glyphicon glyph="export" />
                  <FormattedMessage {...messages.exportFormat} />
                </DropdownToggle>
                <DropdownMenu>{formats}</DropdownMenu>
              </Dropdown>
            )}
          </ButtonToolbar>
        </fieldset>
        <hr />
      </form>
    );
  }
}

export default reduxForm({
  form: 'filterForm',
})(connect()(injectIntl(FilterForm)));
